import merge from 'lodash/merge';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { validateIdentityUniqueness } from '../../util/api';
import {
  getActiveCountryCode,
  normalizeIdentityValue,
  normalizeOfficialStoreIdentityValue,
} from '../../util/identity';
import { fetchCurrentUser, currentUserShowSuccess } from '../../ducks/user.duck';

// ================ Action types ================ //

export const SAVE_CONTACT_DETAILS_REQUEST = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_REQUEST';
export const SAVE_CONTACT_DETAILS_SUCCESS = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_SUCCESS';
export const SAVE_EMAIL_ERROR = 'app/ContactDetailsPage/SAVE_EMAIL_ERROR';
export const SAVE_PHONE_NUMBER_ERROR = 'app/ContactDetailsPage/SAVE_PHONE_NUMBER_ERROR';

export const SAVE_CONTACT_DETAILS_CLEAR = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_CLEAR';

export const RESET_PASSWORD_REQUEST = 'app/ContactDetailsPage/RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'app/ContactDetailsPage/RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_ERROR = 'app/ContactDetailsPage/RESET_PASSWORD_ERROR';

// ================ Reducer ================ //

const initialState = {
  saveEmailError: null,
  savePhoneNumberError: null,
  saveContactDetailsInProgress: false,
  contactDetailsChanged: false,
  resetPasswordInProgress: false,
  resetPasswordError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_CONTACT_DETAILS_REQUEST:
      return {
        ...state,
        saveContactDetailsInProgress: true,
        saveEmailError: null,
        savePhoneNumberError: null,
        contactDetailsChanged: false,
      };
    case SAVE_CONTACT_DETAILS_SUCCESS:
      return { ...state, saveContactDetailsInProgress: false, contactDetailsChanged: true };
    case SAVE_EMAIL_ERROR:
      return { ...state, saveContactDetailsInProgress: false, saveEmailError: payload };
    case SAVE_PHONE_NUMBER_ERROR:
      return { ...state, saveContactDetailsInProgress: false, savePhoneNumberError: payload };

    case SAVE_CONTACT_DETAILS_CLEAR:
      return {
        ...state,
        saveContactDetailsInProgress: false,
        saveEmailError: null,
        savePhoneNumberError: null,
        contactDetailsChanged: false,
      };

    case RESET_PASSWORD_REQUEST:
      return { ...state, resetPasswordInProgress: true, resetPasswordError: null };
    case RESET_PASSWORD_SUCCESS:
      return { ...state, resetPasswordInProgress: false };
    case RESET_PASSWORD_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, resetPasswordInProgress: false, resetPasswordError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const saveContactDetailsRequest = () => ({ type: SAVE_CONTACT_DETAILS_REQUEST });
export const saveContactDetailsSuccess = () => ({ type: SAVE_CONTACT_DETAILS_SUCCESS });
export const saveEmailError = error => ({
  type: SAVE_EMAIL_ERROR,
  payload: error,
  error: true,
});
export const savePhoneNumberError = error => ({
  type: SAVE_PHONE_NUMBER_ERROR,
  payload: error,
  error: true,
});

export const saveContactDetailsClear = () => ({ type: SAVE_CONTACT_DETAILS_CLEAR });

export const resetPasswordRequest = () => ({ type: RESET_PASSWORD_REQUEST });

export const resetPasswordSuccess = () => ({ type: RESET_PASSWORD_SUCCESS });

export const resetPasswordError = e => ({
  type: RESET_PASSWORD_ERROR,
  error: true,
  payload: e,
});

const validateIdentityForContactDetailsUpdate = params => {
  const {
    userType,
    cuit,
    dni,
    storeIdentityField,
    customerIdentityField,
    storeLegacyField,
    customerLegacyField,
  } = params;

  const countryCode = getActiveCountryCode();

  if (userType === 'cliente') {
    const customerIdentity = normalizeIdentityValue(dni);

    if (customerIdentity) {
      return validateIdentityUniqueness({
        userType,
        [customerIdentityField]: customerIdentity,
        ...(customerLegacyField && customerLegacyField !== customerIdentityField
          ? { [customerLegacyField]: customerIdentity }
          : {}),
      });
    }
  }

  if (userType === 'tienda-oficial') {
    const storeIdentity = normalizeOfficialStoreIdentityValue(cuit, countryCode);

    if (storeIdentity) {
      return validateIdentityUniqueness({
        userType,
        [storeIdentityField]: storeIdentity,
        ...(storeLegacyField && storeLegacyField !== storeIdentityField
          ? { [storeLegacyField]: storeIdentity }
          : {}),
      });
    }
  }

  return Promise.resolve();
};

// ================ Thunks ================ //

/**
 * Make a profile details update request to the API and return the current user.
 * Supports phone number (protectedData) and CUIT/DNI (privateData).
 */
const requestSaveProfileDetails = params => (dispatch, getState, sdk) => {
  const {
    phoneNumber,
    cuit,
    dni,
    storeIdentityField,
    customerIdentityField,
    storeLegacyField,
    customerLegacyField,
  } = params;
  const updateParams = {};

  if (typeof phoneNumber !== 'undefined') {
    updateParams.protectedData = { phoneNumber };
  }

  const privateData = {};

  if (typeof cuit !== 'undefined') {
    privateData[storeIdentityField || 'cuit'] = cuit;
    if (storeLegacyField && storeLegacyField !== storeIdentityField) {
      privateData[storeLegacyField] = cuit;
    }
  }

  if (typeof dni !== 'undefined') {
    privateData[customerIdentityField || 'dni'] = dni;
    if (customerLegacyField && customerLegacyField !== customerIdentityField) {
      privateData[customerLegacyField] = dni;
    }
  }

  if (Object.keys(privateData).length > 0) {
    updateParams.privateData = privateData;
  }

  return sdk.currentUser
    .updateProfile(updateParams, {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(savePhoneNumberError(storableError(e)));
      throw e;
    });
};

/**
 * Make a email update request to the API and return the current user.
 */
const requestSaveEmail = params => (dispatch, getState, sdk) => {
  const { email, currentPassword } = params;

  return sdk.currentUser
    .changeEmail(
      { email, currentPassword },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.changeEmail response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(saveEmailError(storableError(e)));
      // pass the same error so that the SAVE_CONTACT_DETAILS_SUCCESS
      // action will not be fired
      throw e;
    });
};

/**
 * Save email and update the current user.
 */
const saveEmail = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSaveEmail(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      // error action dispatched in requestSaveEmail
      .catch(e => null)
  );
};

/**
 * Save profile details (phone number and/or cuit/dni) and update the current user.
 */
const saveProfileDetails = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSaveProfileDetails(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      .catch(e => null)
  );
};

/**
 * Save email and profile details and update the current user.
 */
const saveEmailAndProfileDetails = params => (dispatch, getState, sdk) => {
  const {
    email,
    currentPassword,
    phoneNumber,
    cuit,
    dni,
    storeIdentityField,
    customerIdentityField,
    storeLegacyField,
    customerLegacyField,
  } = params;

  const promises = [
    dispatch(requestSaveEmail({ email, currentPassword })),
    dispatch(
      requestSaveProfileDetails({
        phoneNumber,
        cuit,
        dni,
        storeIdentityField,
        customerIdentityField,
        storeLegacyField,
        customerLegacyField,
      })
    ),
  ];

  return Promise.all(promises)
    .then(values => {
      const saveEmailUser = values[0];
      const saveProfileDetailsUser = values[1];

      const protectedData = saveProfileDetailsUser.attributes.profile.protectedData;
      const privateData = saveProfileDetailsUser.attributes.profile.privateData;
      const mergeSource = { attributes: { profile: { protectedData, privateData } } };

      const currentUser = merge(saveEmailUser, mergeSource);
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(saveContactDetailsSuccess());
    })
    .catch(e => null);
};

/**
 * Update contact details, actions depend on which data has changed
 */
export const saveContactDetails = params => (dispatch, getState, sdk) => {
  dispatch(saveContactDetailsRequest());

  const {
    email,
    currentEmail,
    phoneNumber,
    currentPhoneNumber,
    cuit,
    currentCuit,
    dni,
    currentDni,
    currentPassword,
    userType,
    storeIdentityField,
    customerIdentityField,
    storeLegacyField,
    customerLegacyField,
  } = params;
  const emailChanged = email !== currentEmail;
  const phoneNumberChanged = phoneNumber !== currentPhoneNumber;
  const cuitChanged = userType === 'tienda-oficial' && cuit !== currentCuit;
  const dniChanged = userType === 'cliente' && dni !== currentDni;
  const profileDetailsChanged = phoneNumberChanged || cuitChanged || dniChanged;

  const profileDetailsParams = {
    ...(phoneNumberChanged ? { phoneNumber } : {}),
    ...(cuitChanged ? { cuit } : {}),
    ...(dniChanged ? { dni } : {}),
    ...(cuitChanged
      ? {
          storeIdentityField,
          storeLegacyField,
        }
      : {}),
    ...(dniChanged
      ? {
          customerIdentityField,
          customerLegacyField,
        }
      : {}),
  };

  const identityValidationPromise = profileDetailsChanged
    ? validateIdentityForContactDetailsUpdate({
        userType,
        cuit,
        dni,
        storeIdentityField,
        customerIdentityField,
        storeLegacyField,
        customerLegacyField,
      })
    : Promise.resolve();

  if (emailChanged && profileDetailsChanged) {
    return identityValidationPromise
      .then(() =>
        dispatch(
          saveEmailAndProfileDetails({
            email,
            currentPassword,
            ...profileDetailsParams,
          })
        )
      )
      .catch(e => {
        dispatch(savePhoneNumberError(storableError(e)));
        return null;
      });
  } else if (emailChanged) {
    return dispatch(saveEmail({ email, currentPassword }));
  } else if (profileDetailsChanged) {
    return identityValidationPromise
      .then(() => dispatch(saveProfileDetails(profileDetailsParams)))
      .catch(e => {
        dispatch(savePhoneNumberError(storableError(e)));
        return null;
      });
  }
};

export const resetPassword = email => (dispatch, getState, sdk) => {
  dispatch(resetPasswordRequest());
  return sdk.passwordReset
    .request({ email })
    .then(() => dispatch(resetPasswordSuccess()))
    .catch(e => dispatch(resetPasswordError(storableError(e))));
};

export const loadData = () => {
  // Since verify email happens in separate tab, current user's data might be updated
  return fetchCurrentUser();
};
